/**
 * Speaking a notice in the hub's voice (card `t_92849b4b`).
 *
 * The notice path used `window.speechSynthesis` and set only `lang` + `volume`,
 * so the utterance was voiced by whichever OS voice Electron happened to map to
 * that tag — on this machine six English ones (`Microsoft David/Hazel/Susan/
 * George/Mark/Zira`), never the voice the owner configured for the hub
 * (`POST /api/agent/config` → `ttsVoice` → the hub's Piper addon). Heard on the
 * dev hub: notices read in an English OS voice, the "different voice".
 *
 * The hub is the app's voice authority, and it already serves that voice to
 * clients: its `/wyoming` namespace (what voice satellites speak to) answers
 * `tts:synthesize { text }` with `tts:audio { audio, sampleRate, sampleWidth,
 * channels }` — raw PCM rendered by Piper with `voiceService.config.voice`,
 * i.e. the configured one. Measured on the dev hub: 86 528 B of 22 050 Hz mono
 * PCM (≈1.96 s) for "Garaža: zaznano gibanje" in `sl_SI-artur-medium`.
 *
 * So this module asks the hub for the sentence and plays the audio it gets
 * through the app's one playback path (`playAudioUrl`: queue + master volume).
 * The OS voices remain the fallback for a surface with no hub socket (browser /
 * cloud / before the shell connects) — a fallback, never the first choice.
 *
 * Pure and transport-injectable on purpose: `hubVoice.test.ts` asserts the WAV
 * container, the hub origin, and every terminal path (audio, hub error,
 * connection error, timeout) without a hub, a socket or a DOM.
 */
import { io } from 'socket.io-client';
import { playAudioUrl } from './audioPlayback';

/** One synthesized sentence: PCM already wrapped as a playable WAV. */
export interface HubSpeechClip {
  wav: Uint8Array;
  sampleRate: number;
  sampleWidth: number;
  channels: number;
  /** Bytes of PCM (before the 44-byte WAV header) — for logs and probes. */
  pcmBytes: number;
}

/** The socket.io client this module needs; `socket.io-client`'s Socket fits. */
export interface WyomingClientLike {
  on(event: string, listener: (data?: unknown) => void): unknown;
  off?(event: string, listener?: (data?: unknown) => void): unknown;
  emit(event: string, ...args: unknown[]): unknown;
  disconnect?(): unknown;
  connect?(): unknown;
}

/** Opens a client for the hub's voice namespace. Injectable for tests. */
export type WyomingClientFactory = (url: string) => WyomingClientLike;

/** The hub namespace that answers `tts:synthesize`. */
export const WYOMING_NAMESPACE = '/wyoming';

/** How long a notice waits for its own audio before the OS voice speaks. */
export const HUB_SPEECH_TIMEOUT_MS = 8000;

function toUint8Array(value: unknown): Uint8Array | null {
  if (value == null) return null;
  if (value instanceof Uint8Array) return value;
  if (value instanceof ArrayBuffer) return new Uint8Array(value);
  if (ArrayBuffer.isView(value)) {
    return new Uint8Array(value.buffer, value.byteOffset, value.byteLength);
  }
  // A Buffer serialized by socket.io without binary support arrives as
  // `{ type: 'Buffer', data: [...] }` (and, from some transports, a plain array).
  if (Array.isArray(value)) return Uint8Array.from(value as number[]);
  const record = value as { type?: unknown; data?: unknown };
  if (record.type === 'Buffer' && Array.isArray(record.data)) return Uint8Array.from(record.data as number[]);
  return null;
}

/** The reason a socket.io failure event carries, whichever shape it arrived in. */
function readReason(data: unknown): string | null {
  if (typeof data === 'string' && data) return data;
  if (data instanceof Error) return data.message;
  const message = (data as { message?: unknown } | null | undefined)?.message;
  return typeof message === 'string' && message ? message : null;
}

/**
 * Wrap raw PCM in a RIFF/WAVE container — 44-byte canonical header (PCM, no
 * extra chunks) followed by the samples, so `decodeAudioData` and every audio
 * element can play it.
 */
export function pcmToWav(pcm: Uint8Array, sampleRate: number, sampleWidth = 2, channels = 1): Uint8Array {
  const header = new Uint8Array(44);
  const view = new DataView(header.buffer);
  const byteRate = sampleRate * channels * sampleWidth;
  const blockAlign = channels * sampleWidth;

  const writeAscii = (offset: number, text: string): void => {
    for (let i = 0; i < text.length; i += 1) view.setUint8(offset + i, text.charCodeAt(i));
  };

  writeAscii(0, 'RIFF');
  view.setUint32(4, 36 + pcm.length, true);
  writeAscii(8, 'WAVE');
  writeAscii(12, 'fmt ');
  view.setUint32(16, 16, true); // PCM fmt chunk size
  view.setUint16(20, 1, true); // format = PCM
  view.setUint16(22, channels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, sampleWidth * 8, true);
  writeAscii(36, 'data');
  view.setUint32(40, pcm.length, true);

  const wav = new Uint8Array(header.length + pcm.length);
  wav.set(header, 0);
  wav.set(pcm, header.length);
  return wav;
}

/**
 * The hub origin the given surface's socket points at, or null when the surface
 * holds no socket.io connection of its own (the WebRTC status bridge has no
 * manager). The uri is what socket.io was handed: an absolute hub URL in
 * Electron/Capacitor, `/` in browser-local mode — resolved here against the
 * page origin, which in that mode *is* the hub.
 */
export function hubVoiceOrigin(socket: unknown): string | null {
  const uri = (socket as { io?: { uri?: unknown } } | null | undefined)?.io?.uri;
  if (typeof uri !== 'string' || !uri) return null;
  try {
    const base = typeof location !== 'undefined' ? location.origin : undefined;
    return new URL(uri, base).origin;
  } catch {
    return null;
  }
}

function clipFromAudioPayload(data: unknown): HubSpeechClip | null {
  if (data == null || typeof data !== 'object') return null;
  const payload = data as { audio?: unknown; sampleRate?: unknown; sampleWidth?: unknown; channels?: unknown };
  const pcm = toUint8Array(payload.audio);
  if (!pcm || pcm.length === 0) return null;

  const sampleRate = Number(payload.sampleRate) || 22050;
  const sampleWidth = Number(payload.sampleWidth) || 2;
  const channels = Number(payload.channels) || 1;
  return {
    wav: pcmToWav(pcm, sampleRate, sampleWidth, channels),
    sampleRate,
    sampleWidth,
    channels,
    pcmBytes: pcm.length,
  };
}

/** Opens the namespace on the hub with the same transports the app's socket uses. */
const defaultClientFactory: WyomingClientFactory = (url) =>
  io(url, { transports: ['websocket', 'polling'], reconnection: false, timeout: 4000 });

export interface HubSpeechRequest {
  origin: string;
  text: string;
  timeoutMs?: number;
  createClient?: WyomingClientFactory;
}

/**
 * Ask the hub to speak `text` in its configured voice. Resolves with the clip,
 * or null on every path that leaves nothing to play (hub error, no connection,
 * timeout, empty audio). The namespace client is closed in all of them: a
 * notice is a sentence, not a subscription.
 */
export async function requestHubSpeech(request: HubSpeechRequest): Promise<HubSpeechClip | null> {
  const text = request.text?.trim();
  if (!text || !request.origin) return null;

  const create = request.createClient ?? defaultClientFactory;
  const url = `${request.origin.replace(/\/+$/, '')}${WYOMING_NAMESPACE}`;

  let client: WyomingClientLike;
  try {
    client = create(url);
  } catch {
    return null;
  }

  return new Promise<HubSpeechClip | null>((resolve) => {
    let settled = false;
    const timeoutMs = request.timeoutMs ?? HUB_SPEECH_TIMEOUT_MS;
    const timer = setTimeout(() => {
      console.warn(
        `[hubVoice] No synthesis from the hub within ${timeoutMs} ms — the OS voice speaks instead`,
      );
      finish(null);
    }, timeoutMs);

    function finish(clip: HubSpeechClip | null): void {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      try {
        client.off?.('tts:audio', onAudio);
        client.off?.('error', onFailure);
        client.off?.('connect_error', onFailure);
        client.disconnect?.();
      } catch {
        // A client that already tore itself down needs nothing from us.
      }
      resolve(clip);
    }

    function onAudio(data?: unknown): void {
      finish(clipFromAudioPayload(data));
    }

    // The hub answers `error { message }` when its voice service is down
    // (WyomingServer), and `connect_error` when the namespace is not reachable.
    // The reason is logged rather than swallowed: it is the difference between
    // "the hub cannot speak" (its own words name the missing service) and a
    // client-side mystery, and the OS voice takes over either way.
    function onFailure(reason?: unknown): void {
      console.warn(
        `[hubVoice] The hub did not synthesize — ${readReason(reason) ?? 'no reason given'} (the OS voice speaks instead)`,
      );
      finish(null);
    }

    client.on('tts:audio', onAudio);
    client.on('error', onFailure);
    client.on('connect_error', onFailure);
    client.emit('tts:synthesize', { text });
  });
}

/**
 * Play a clip through the app's audio path (queue + master volume, `AudioContext`).
 * The blob URL lives exactly as long as the playback.
 */
export async function playHubVoiceClip(clip: HubSpeechClip, priority: 'normal' | 'urgent' = 'normal'): Promise<void> {
  const url = URL.createObjectURL(new Blob([clip.wav as BlobPart], { type: 'audio/wav' }));
  try {
    await playAudioUrl(url, priority);
  } finally {
    URL.revokeObjectURL(url);
  }
}

export interface SpeakInHubVoiceOptions {
  createClient?: WyomingClientFactory;
  play?: (clip: HubSpeechClip) => Promise<void>;
  timeoutMs?: number;
}

/**
 * The whole decision for one utterance: does this surface have a hub to speak
 * through, does the hub answer with audio, did it play? `false` means the
 * caller should fall back to the OS voices — never that the notice went silent
 * for a reason of ours.
 */
export async function speakInHubVoice(
  socket: unknown,
  text: string,
  options: SpeakInHubVoiceOptions = {},
): Promise<boolean> {
  const origin = hubVoiceOrigin(socket);
  if (!origin) return false;

  const clip = await requestHubSpeech({
    origin,
    text,
    timeoutMs: options.timeoutMs,
    createClient: options.createClient,
  });
  if (!clip) return false;

  const play = options.play ?? playHubVoiceClip;
  try {
    await play(clip);
    return true;
  } catch {
    // Decoding/playback failed — the utterance is still owed, so let the
    // caller speak it with the OS voice rather than dropping it.
    return false;
  }
}
